<template>
  <v-container
    :class="[containerClass, slotFillsHeight && 'standard-page-template--fill-root']"
    fluid
  >
    <slot
      v-if="!noTitle"
      name="title"
    >
      <PageTitle
        :back-back-name="backBackName"
        :back-back-route="backBackRoute"
        :back-name="backName"
        :back-route="backRoute"
        :title="pageTitle"
        data-testid="standard-page-template-title"
        flat
        outlined
      />
    </slot>

    <v-container
      :class="innerContainerClass"
      fluid
    >
      <div
        v-if="hasSelect || hasSearch || hasActionButton"
        :class="[
          'd-flex flex-column flex-md-row align-md-center ga-2',
          slotFillsHeight && 'flex-shrink-0',
        ]"
        data-testid="standard-page-template-toolbar"
      >
        <div
          v-if="hasSelect || hasSearch"
          class="d-flex flex-column flex-md-row align-stretch align-md-center ga-2 min-w-0 w-100 w-md-auto flex-md-fill standard-page-template--toolbar-start"
        >
          <slot
            v-if="hasSelect"
            name="select"
          >
            <div
              v-if="select"
              class="standard-page-template--select-root min-w-0 w-100 w-md-auto flex-grow-0 flex-shrink-0"
            >
              <StandardSelect
                v-model="selectModel"
                :aria-label="selectLabelTranslated"
                :data-testid="selectTestId"
                :input-class="select.inputClass ?? 'w-100 mxw-350'"
                :item-title="select.itemTitle ?? 'name'"
                :item-value="select.itemValue ?? 'id'"
                :items="select.items ?? []"
                :placeholder="selectLabelTranslated"
              />
            </div>
          </slot>

          <slot
            v-if="hasSearch"
            name="search"
          >
            <div
              v-if="search"
              class="standard-page-template--search-root min-w-0 w-100"
            >
              <StandardSearch
                v-model="searchModel"
                :aria-label="searchLabelTranslated"
                :data-testid="searchTestId"
                :hide-no-data="search.hideNoData ?? false"
                :input-class="search.inputClass ?? 'w-100 mxw-400'"
                :item-title="search.itemTitle ?? 'title'"
                :item-value="search.itemValue ?? 'value'"
                :items="search.items ?? []"
                :loading="searchFieldLoading"
                :on-search-input="search.onSearchInput"
                :placeholder="searchLabelTranslated"
                :variant="search.variant ?? 'text'"
              />
            </div>
          </slot>
        </div>

        <template v-if="hasActionButton">
          <div
            :class="[
              'w-100 w-md-auto d-flex align-center ga-2 justify-end flex-md-0-0',
              !hasSelect && !hasSearch && 'ms-md-auto',
            ]"
            data-testid="standard-page-template-toolbar-actions"
          >
            <slot name="secondary-button">
              <StandardButton
                v-if="props.secondaryButton"
                @click="$emit('secondary')"
                :aria-label="props.secondaryButton.ariaLabel"
                :data-testid="
                  props.secondaryButton.testId || 'standard-page-template-secondary-button'
                "
                :disabled-text="props.secondaryButton.disabledText"
                :loading="processing"
                :prepend-icon="props.secondaryButton.prependIcon"
                :role="props.secondaryButton.role || 'secondary'"
                :text="props.secondaryButton.text"
              />
            </slot>

            <slot name="primary-button">
              <StandardButton
                v-if="createable"
                @click="$emit('create')"
                :loading="processing"
                :text="`New ${resourceName}`"
                data-testid="standard-page-template-create-button"
                role="create"
              />

              <StandardButton
                v-else-if="props.primaryButton"
                @click="$emit('primary')"
                :aria-label="props.primaryButton.ariaLabel"
                :data-testid="props.primaryButton.testId || 'standard-page-template-primary-button'"
                :density="props.primaryButton.density"
                :disabled-text="props.primaryButton.disabledText"
                :icon="props.primaryButton.icon"
                :icon-size="props.primaryButton.iconSize"
                :loading="processing"
                :prepend-icon="props.primaryButton.prependIcon"
                :role="props.primaryButton.role || 'primary'"
                :size="props.primaryButton.size"
                :text="props.primaryButton.text"
              />
            </slot>
          </div>
        </template>
      </div>

      <div
        v-if="slotFillsHeight"
        class="standard-page-template--fill-main"
      >
        <div class="standard-page-template--fill-main-inner">
          <slot />
        </div>
      </div>
      <slot v-else />
    </v-container>
  </v-container>
</template>

<script setup>
import { useSlots } from 'vue';

import { capitalize, pluralize } from '@/plugins/filters';
import PageTitle from '@/shared/components/PageTitle.vue';

const $bct = inject('translator');
const slots = useSlots();

const props = defineProps({
  backBackName: {
    type: String,
    default: null,
  },
  backBackRoute: {
    type: Object,
    default: null,
  },
  backName: {
    type: String,
    default: null,
  },
  backRoute: {
    type: Object,
    default: null,
  },
  containerClass: {
    type: String,
    default: 'px-0 py-0 bg-super-light-blue',
  },
  createable: {
    type: Boolean,
    default: false,
  },
  margin: {
    type: Number,
    default: null,
  },
  noTitle: {
    type: Boolean,
    default: false,
  },
  plainPageTitle: {
    type: Boolean,
    default: false,
  },
  primaryButton: {
    type: Object,
    default: null,
  },
  processing: {
    type: Boolean,
    default: false,
  },
  resourceName: {
    type: String,
    default: null,
  },
  search: {
    type: Object,
    default: null,
  },
  secondaryButton: {
    type: Object,
    default: null,
  },
  select: {
    type: Object,
    default: null,
  },
  slotFillsHeight: {
    type: Boolean,
    default: false,
  },
  spacing: {
    type: [String, Number],
    default: 3,
  },
  title: {
    type: String,
    default: null,
  },
});

defineEmits(['create', 'primary', 'secondary']);

const hasActionButton = computed(() => {
  return Boolean(
    props.createable ||
      props.primaryButton ||
      props.secondaryButton ||
      slots['primary-button'] ||
      slots['secondary-button'],
  );
});

const hasSearch = computed(() => Boolean(props.search || slots.search));

const hasSelect = computed(() => Boolean(props.select || slots.select));

const innerContainerClass = computed(() => {
  const margin = props.margin ?? props.spacing;
  const base = props.slotFillsHeight
    ? `pt-${margin} px-${margin} pb-${margin} d-flex flex-column ga-${props.spacing}`
    : `my-${margin} px-${margin} py-0 d-flex flex-column ga-${props.spacing}`;

  return [base, props.slotFillsHeight && 'standard-page-template--fill-body'];
});

const pageTitle = computed(() => {
  if (props.plainPageTitle && props.title != null && props.title !== '') return props.title;

  return capitalize($bct(props.title || pluralize(props.resourceName)));
});

const searchLabelTranslated = computed(() => {
  let label = 'Search';

  if (props.search?.label) {
    label = props.search.label;
  } else if (props.resourceName) {
    label = label + ' ' + pluralize(props.resourceName);
  }

  return $bct(label);
});

const searchTestId = computed(() => {
  return (
    props.search?.testId ||
    (props.resourceName ? `${props.resourceName}-search` : 'standard-page-template-search')
  );
});

const searchFieldLoading = computed(() => {
  if (props.search && 'loading' in props.search && props.search.loading != null) {
    return Boolean(props.search.loading);
  }
  return props.processing;
});

const searchModel = computed({
  get() {
    return props.search?.modelValue;
  },
  set(value) {
    props.search?.updateHandler(value);
  },
});

const selectLabelTranslated = computed(() => {
  return $bct(props.select?.label || 'Select');
});

const selectTestId = computed(() => {
  return props.select?.testId || 'standard-page-template-select';
});

const selectModel = computed({
  get() {
    return props.select?.modelValue;
  },
  set(value) {
    props.select?.updateHandler(value);
  },
});
</script>

<style scoped>
.standard-page-template--fill-root,
.standard-page-template--fill-main-inner,
.standard-page-template--fill-main-inner :deep(.data-table-server) {
  display: flex;
  flex: 1 1 0;
  flex-direction: column;
  height: 100%;
  min-height: 0;
}

.standard-page-template--fill-body,
.standard-page-template--fill-main-inner :deep(.data-table-server .v-data-table),
.standard-page-template--fill-main-inner :deep(.data-table-server .v-table) {
  flex: 1 1 0;
  min-height: 0;
}

.standard-page-template--fill-main {
  display: flex;
  flex: 1 1 0;
  flex-direction: column;
  min-height: 0;
  min-width: 0;
}

@media (min-width: 960px) {
  .standard-page-template--toolbar-start > * {
    min-width: 0;
  }

  .standard-page-template--search-root {
    flex: 1 1 auto;
    max-width: 400px;
  }
}
</style>
